import {
  getOverflowRevealKeyframes,
  getOverflowRevealTiming,
  OVERFLOW_REVEAL_FALLBACK_FONT_SIZE_PX,
  OverflowRevealKeyframes,
  OverflowRevealTiming
} from "./overflow-reveal";

/**
 * Runtime half of the overflow reveal: measures whether a label overflows, marks it so the CSS can
 * put the gradient fade on it, and runs the slide while its row is hovered or focused.
 *
 * Measurement runs off a SINGLE module-level ResizeObserver shared by every instance, and the
 * callback reads all sizes before it writes any attribute — a designer panel can hold hundreds of
 * rows, and interleaving reads with writes would re-run layout once per row on every panel resize.
 * Nothing here touches React state, so a measurement never re-renders a list.
 */

const REDUCED_MOTION_QUERY: string = "(prefers-reduced-motion: reduce)";

/** Rows opt in by carrying this class; the label then reacts to the whole row's hover, not its own. */
export const OVERFLOW_REVEAL_HOST_CLASS: string = "kuika-overflow-text-host";

/**
 * The browser's own "this focus came from the keyboard". A row is focusable in every list since the
 * roving tabindex landed, so a plain click focuses one too — and a slide started on that focus would
 * keep looping long after the pointer had left, because the row stays focused.
 */
const KEYBOARD_FOCUS_SELECTOR: string = ":focus-visible";

interface OverflowMeasurement {
  overflowPx: number;
  fontSizePx: number;
}

const prefersReducedMotion = (): boolean => {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return false;
  }

  return window.matchMedia(REDUCED_MOTION_QUERY).matches;
};

/** `:focus-visible` is unsupported in some test environments, where "not keyboard" is the right answer. */
const isKeyboardFocus = (element: Element): boolean => {
  try {
    return element.matches(KEYBOARD_FOCUS_SELECTOR);
  } catch {
    return false;
  }
};

export class OverflowRevealController {
  private readonly root: HTMLElement;

  private readonly track: HTMLElement;

  private readonly host: HTMLElement;

  private measurement: OverflowMeasurement = { overflowPx: 0, fontSizePx: OVERFLOW_REVEAL_FALLBACK_FONT_SIZE_PX };

  private animations: Animation[] = [];

  private playing: boolean = false;

  /** Pointer and keyboard hold the slide independently: either one keeps it running. */
  private pointerInside: boolean = false;

  private keyboardFocused: boolean = false;

  constructor(root: HTMLElement, track: HTMLElement) {
    this.root = root;
    this.track = track;
    this.host = root.closest<HTMLElement>(`.${OVERFLOW_REVEAL_HOST_CLASS}`) ?? root;
  }

  attach(): void {
    this.host.addEventListener("mouseenter", this.handlePointerEnter);
    this.host.addEventListener("mouseleave", this.handlePointerLeave);
    this.host.addEventListener("focusin", this.handleFocusIn);
    this.host.addEventListener("focusout", this.handleFocusOut);

    observe(this.root, this);
    observe(this.track, this);

    this.commit(this.read());
  }

  detach(): void {
    this.host.removeEventListener("mouseenter", this.handlePointerEnter);
    this.host.removeEventListener("mouseleave", this.handlePointerLeave);
    this.host.removeEventListener("focusin", this.handleFocusIn);
    this.host.removeEventListener("focusout", this.handleFocusOut);

    unobserve(this.root);
    unobserve(this.track);

    this.stop();
  }

  /** Read phase: no DOM writes, so a batch of controllers can measure without thrashing layout. */
  read(): OverflowMeasurement {
    const trackWidth: number = this.track.getBoundingClientRect().width || this.track.scrollWidth;
    const rootWidth: number = this.root.getBoundingClientRect().width || this.root.clientWidth;
    // A sub-pixel difference is a rounding artefact, not an overflow: fading and sliding a name
    // that visually fits reads as a glitch.
    const measuredOverflowPx: number = rootWidth > 0 ? trackWidth - rootWidth : 0;
    const overflowPx: number = measuredOverflowPx > 1 ? measuredOverflowPx : 0;
    const fontSize: number = Number.parseFloat(window.getComputedStyle(this.track).fontSize);

    return {
      overflowPx,
      fontSizePx: Number.isFinite(fontSize) && fontSize > 0 ? fontSize : OVERFLOW_REVEAL_FALLBACK_FONT_SIZE_PX
    };
  }

  /** Write phase. A size change mid-slide restarts the slide against the new distance. */
  commit(measurement: OverflowMeasurement): void {
    const wasPlaying: boolean = this.playing;

    this.measurement = measurement;
    this.cancelAnimations();
    this.root.toggleAttribute("data-overflowing", measurement.overflowPx > 0);

    if (measurement.overflowPx <= 0) {
      this.playing = false;

      return;
    }

    if (wasPlaying || this.pointerInside || this.keyboardFocused) {
      this.play();
    }
  }

  private play(): void {
    if (this.measurement.overflowPx <= 0 || typeof this.root.animate !== "function") {
      return;
    }

    this.playing = true;

    if (prefersReducedMotion()) {
      return;
    }

    const timing: OverflowRevealTiming = getOverflowRevealTiming(
      this.measurement.overflowPx,
      this.measurement.fontSizePx
    );
    const keyframes: OverflowRevealKeyframes = getOverflowRevealKeyframes(this.measurement.overflowPx, timing);
    const options: KeyframeAnimationOptions = { duration: timing.durationMs, iterations: Infinity };

    this.animations = [this.track.animate(keyframes.track, options), this.root.animate(keyframes.mask, options)];
  }

  private stop(): void {
    this.playing = false;
    this.cancelAnimations();
  }

  private cancelAnimations(): void {
    this.animations.forEach((animation: Animation): void => animation.cancel());
    this.animations = [];
  }

  private start(): void {
    if (this.playing) {
      return;
    }

    this.play();
  }

  /** Hover can end while the keyboard stays on the row (and the other way round); either one wins. */
  private stopUnlessHeld(): void {
    if (this.pointerInside || this.keyboardFocused) {
      return;
    }

    this.stop();
  }

  private handlePointerEnter = (): void => {
    this.pointerInside = true;
    this.start();
  };

  private handlePointerLeave = (): void => {
    this.pointerInside = false;
    this.stopUnlessHeld();
  };

  /**
   * Only keyboard focus slides. `event.target` is what actually took focus — a row action button
   * inside the row counts, because the row is what the label belongs to.
   */
  private handleFocusIn = (event: FocusEvent): void => {
    const focused: Element | null = event.target instanceof Element ? event.target : null;

    if (focused === null || !isKeyboardFocus(focused)) {
      return;
    }

    this.keyboardFocused = true;
    this.start();
  };

  private handleFocusOut = (): void => {
    this.keyboardFocused = false;
    this.stopUnlessHeld();
  };
}

const controllersByElement: WeakMap<Element, OverflowRevealController> = new WeakMap();

let sharedObserver: ResizeObserver | null = null;

const handleResize = (entries: ResizeObserverEntry[]): void => {
  const controllers: Set<OverflowRevealController> = new Set();

  entries.forEach((entry: ResizeObserverEntry): void => {
    const controller: OverflowRevealController | undefined = controllersByElement.get(entry.target);

    if (controller) {
      controllers.add(controller);
    }
  });

  const pending: [OverflowRevealController, OverflowMeasurement][] = [];

  controllers.forEach((controller: OverflowRevealController): void => {
    pending.push([controller, controller.read()]);
  });

  pending.forEach(([controller, measurement]: [OverflowRevealController, OverflowMeasurement]): void => {
    controller.commit(measurement);
  });
};

const getSharedObserver = (): ResizeObserver | null => {
  if (typeof ResizeObserver === "undefined") {
    return null;
  }

  if (!sharedObserver) {
    sharedObserver = new ResizeObserver(handleResize);
  }

  return sharedObserver;
};

const observe = (element: Element, controller: OverflowRevealController): void => {
  controllersByElement.set(element, controller);
  getSharedObserver()?.observe(element);
};

const unobserve = (element: Element): void => {
  controllersByElement.delete(element);
  getSharedObserver()?.unobserve(element);
};

/** Test seam: the observer is module state, so a suite that stubs `ResizeObserver` must reset it. */
export const resetOverflowRevealObserverForTests = (): void => {
  sharedObserver = null;
};
